import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

import { getContentForRichtextCell } from '~shared/types/Cell'

import { getCell } from '@/features/rundown/data/cells.store'

import { gridStateActions } from '../../../grid/grid.store'
import type { ModalComponentProps } from '../../../modals/modal.store'
import { CellKey } from '../../cell.schema'

export function EditorConflictDialog({
  props,
  onResolve,
}: ModalComponentProps<'EDITOR_CONFLICT_DIALOG'>) {
  const { cellKey } = props

  return (
    <AlertDialog
      open={true}
      onOpenChange={() => {
        onResolve(false) // Cancelled
      }}
    >
      <AlertDialogContent className="min-w-5/6 sm:max-w-xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Content conflict</AlertDialogTitle>
          <AlertDialogDescription>
            The contents of this cell were modified elsewhere after you started
            editing it.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="-mx-4 -mb-4 no-scrollbar max-h-[70vh] overflow-y-auto px-4">
          <div className="text-sm text-muted-foreground">
            <p>You have two choices, please choose carefully:</p>

            <ul>
              <li>
                • You can <b>discard</b> your changes and reload the latest cell
                content.
              </li>
              <li>
                • You can <b>save</b> your changes, overwriting the latest cell
                content.
              </li>
            </ul>
          </div>

          <ContentDiff cellKey={cellKey} />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel
            variant="secondary"
            onClick={() => {
              gridStateActions.editor.discard()
            }}
          >
            Discard my changes
          </AlertDialogCancel>

          <AlertDialogAction
            variant="destructive"
            onClick={() => {
              onResolve(true) // Confirmed

              gridStateActions.editor.finishEditing('force')
            }}
          >
            Save and overwrite
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function ContentDiff({ cellKey }: { cellKey: CellKey }) {
  const cell = getCell(cellKey)

  const latestContent = getContentForRichtextCell(cell?.content)

  const currentEditorContent = gridStateActions.editor.getEditorContent() ?? '' // TODO: Better handling of failure

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="">
        <div className="text-sm font-medium mb-1">Latest content:</div>
        <div className="bg-gray-700/30 border border-gray-800 rounded-md p-3 flex gap-3">
          <p className="text-xs">{latestContent}</p>
        </div>
      </div>
      <div>
        <div className="text-sm font-medium mb-1">Your unsaved content:</div>
        <div className="bg-red-600/10 border border-gray-800 rounded-md p-3 flex gap-3">
          <p className="text-xs">{currentEditorContent}</p>
        </div>
      </div>
    </div>
  )
}
